import { Injectable, inject } from "@angular/core";
import { HttpClient } from "@angular/common/http";
import { firstValueFrom } from "rxjs/internal/firstValueFrom";
import { ApiBaseUrls, ApiControllers } from "../../constants/constants";
import { BankConnectionSortOptions } from "../../enums/sort-options.enums";
import { BankConnectionsFilter } from "../../filters/bank-connections.filter";
import {
  BankConnectionModel,
  BankConnectionSessionResponse,
  BankConnectionSummaryModel,
  CreateBankConnectionRequest,
  CreateBankConnectionSessionRequest,
  ReconnectBankConnectionRequest,
  RefreshBankConnectionsRequest,
  UpdateBankConnectionRequest
} from "../../models/data/bank-connection.model";
import { AuditLog } from "../../models/service/audit-log.model";
import { BankTransactionAuditLogModel } from "../../models/data/bank-transaction-audit-log.model";
import { PaginatedListModel } from "../../models/service/paginated-list.model";
import { PaginationResponseHeader } from "../../models/service/pagination-response-header.model";
import { ServiceResultModel } from "../../models/service/service-result.model";
import { APISanitizerUtilities } from "../../utilities/api-sanitizer.utilities";
import { AuditLogMapper } from "../../utilities/audit-log-mapper.utilities";
import { ServiceUtilities } from "../../utilities/service.utilities";

@Injectable({
  providedIn: 'root'
})
export class BankConnectionService {
  private httpClient = inject(HttpClient);

  private apiPath: string = `${ApiBaseUrls.CORE()}${ApiControllers.BANK_CONNECTIONS()}`;

  /**
   * Starts the Stripe Financial Connections flow and returns the client secret that drives it. The
   * session is platform-scoped, so the browser needs no connected-account context beyond the secret.
   *
   * Fails with 422 when the organization has not completed Stripe onboarding — Financial Connections
   * hangs off the connected account, so it is a prerequisite rather than something this creates.
   */
  public async createBankConnectionSession(
    request: CreateBankConnectionSessionRequest
  ): Promise<ServiceResultModel<BankConnectionSessionResponse>> {
    try {
      const requestUrl = `${this.apiPath}/session`;
      const response = await firstValueFrom(
        this.httpClient.post<BankConnectionSessionResponse>(
          requestUrl,
          APISanitizerUtilities.sanitizePayload(request),
          { observe: "response", headers: { 'Accept': 'application/json' } }
        )
      );

      const session = <BankConnectionSessionResponse>response.body?.valueOf();
      return <ServiceResultModel<BankConnectionSessionResponse>>{ data: session, success: true, statusCode: response.status };
    } catch (error) {
      return ServiceUtilities.handleServiceException(error);
    }
  }

  /** Records the account Stripe's hosted flow linked, against the GL account that designates it. */
  public async createBankConnection(
    request: CreateBankConnectionRequest
  ): Promise<ServiceResultModel<BankConnectionModel>> {
    try {
      const response = await firstValueFrom(
        this.httpClient.post<BankConnectionModel>(
          this.apiPath,
          APISanitizerUtilities.sanitizePayload(request),
          { observe: "response", headers: { 'Accept': 'application/json' } }
        )
      );

      const connection = <BankConnectionModel>response.body?.valueOf();
      return <ServiceResultModel<BankConnectionModel>>{ data: connection, success: true, statusCode: response.status };
    } catch (error) {
      return ServiceUtilities.handleServiceException(error);
    }
  }

  /**
   * Re-points an existing connection at a freshly authenticated account. The GL mapping is preserved
   * server-side, so unlike the initial connect there is no account to choose again.
   *
   * A disconnected account cannot be reactivated — Stripe issues a new account id — and the API
   * re-points the row rather than making the organization rebuild the mapping.
   */
  public async reconnectBankConnection(
    bankConnectionId: number,
    request: ReconnectBankConnectionRequest
  ): Promise<ServiceResultModel<BankConnectionModel>> {
    try {
      const requestUrl = `${this.apiPath}/${bankConnectionId}/reconnect`;
      const response = await firstValueFrom(
        this.httpClient.post<BankConnectionModel>(
          requestUrl,
          APISanitizerUtilities.sanitizePayload(request),
          { observe: "response", headers: { 'Accept': 'application/json' } }
        )
      );

      const connection = <BankConnectionModel>response.body?.valueOf();
      return <ServiceResultModel<BankConnectionModel>>{ data: connection, success: true, statusCode: response.status };
    } catch (error) {
      return ServiceUtilities.handleServiceException(error);
    }
  }

  /** Changes only the GL account that designates a connection. */
  public async updateBankConnection(
    bankConnectionId: number,
    request: UpdateBankConnectionRequest
  ): Promise<ServiceResultModel<BankConnectionModel>> {
    try {
      const requestUrl = `${this.apiPath}/${bankConnectionId}`;
      const response = await firstValueFrom(
        this.httpClient.patch<BankConnectionModel>(
          requestUrl,
          APISanitizerUtilities.sanitizePayload(request),
          { observe: "response", headers: { 'Accept': 'application/json' } }
        )
      );

      const connection = <BankConnectionModel>response.body?.valueOf();
      return <ServiceResultModel<BankConnectionModel>>{ data: connection, success: true, statusCode: response.status };
    } catch (error) {
      return ServiceUtilities.handleServiceException(error);
    }
  }

  /**
   * Disconnects at Stripe and drops the connection. Not reversible in place: reconnecting the same
   * bank later means running the whole connect flow again, GL choice included.
   */
  public async deleteBankConnection(bankConnectionId: number): Promise<ServiceResultModel<boolean>> {
    try {
      const requestUrl = `${this.apiPath}/${bankConnectionId}`;
      const response = await firstValueFrom(
        this.httpClient.delete<void>(
          requestUrl,
          { observe: "response", headers: { 'Accept': 'application/json' } }
        )
      );

      return <ServiceResultModel<boolean>>{ data: true, success: true, statusCode: response.status };
    } catch (error) {
      return ServiceUtilities.handleServiceException(error);
    }
  }

  /**
   * Re-reads every connection's status from Stripe and returns the refreshed set. Gated on
   * stripe.read rather than stripe.connect_bank, so a viewer who cannot manage can still check.
   */
  public async refreshBankConnections(
    request: RefreshBankConnectionsRequest
  ): Promise<ServiceResultModel<BankConnectionModel[]>> {
    try {
      const requestUrl = `${this.apiPath}/refresh`;
      const response = await firstValueFrom(
        this.httpClient.post<BankConnectionModel[]>(
          requestUrl,
          APISanitizerUtilities.sanitizePayload(request),
          { observe: "response", headers: { 'Accept': 'application/json' } }
        )
      );

      const connections = <BankConnectionModel[]>response.body?.valueOf();
      return <ServiceResultModel<BankConnectionModel[]>>{ data: connections ?? [], success: true, statusCode: response.status };
    } catch (error) {
      return ServiceUtilities.handleServiceException(error);
    }
  }

  /**
   * The reconciliation position behind the header buckets, bounded by the statement date.
   *
   * The date and time zone are query parameters rather than body fields because nothing is stored:
   * the statement date is whatever the user typed this session, and the org time zone decides which
   * calendar day a transaction falls on. Gated on bank_reconciliation.read, not stripe.read.
   */
  public async getBankConnectionSummary(
    bankConnectionId: number,
    statementDate?: string,
    timeZoneId?: string
  ): Promise<ServiceResultModel<BankConnectionSummaryModel>> {
    try {
      const query = new URLSearchParams();
      if (statementDate) query.set('statementDate', statementDate);
      if (timeZoneId) query.set('timeZoneId', timeZoneId);

      const search = query.toString();
      const requestUrl = `${this.apiPath}/${bankConnectionId}/summary${search ? `?${search}` : ''}`;

      const response = await firstValueFrom(
        this.httpClient.get<BankConnectionSummaryModel>(
          requestUrl,
          { observe: "response", headers: { 'Accept': 'application/json' } }
        )
      );

      const summary = <BankConnectionSummaryModel>response.body?.valueOf();
      return <ServiceResultModel<BankConnectionSummaryModel>>{ data: summary, success: true, statusCode: response.status };
    } catch (error) {
      return ServiceUtilities.handleServiceException(error);
    }
  }

  /**
   * The reconciliation actions taken on one connected account, newest first.
   *
   * Gated on bank_reconciliation.read, the same permission that opens the page. `includeDetails`
   * asks for the acting user and the note, which the route withholds unless the caller also holds
   * audits.read_details — so the fields it governs stay optional on the model.
   *
   * Mapped onto the shared AuditLog shape, which is what ms-audit-logs renders for every other
   * entity. The connection is the entity: the log is read per account, and the transaction each
   * action was taken on is already named in the action text.
   */
  public async getBankConnectionLogs(
    bankConnectionId: number,
    skip: number,
    take: number,
    includeDetails: boolean = false
  ): Promise<ServiceResultModel<PaginatedListModel<AuditLog>>> {
    try {
      const query = new URLSearchParams({ skip: `${skip}`, take: `${take}` });
      if (includeDetails) query.set('includeDetails', 'true');

      const requestUrl = `${this.apiPath}/${bankConnectionId}/logs?${query}`;

      const response = await firstValueFrom(
        this.httpClient.get<BankTransactionAuditLogModel[]>(
          requestUrl,
          { observe: "response", headers: { 'Accept': 'application/json' } }
        )
      );

      const paginationHeader: PaginationResponseHeader = JSON.parse(response.headers.get("x-pagination")!);
      const paginatedList: PaginatedListModel<AuditLog> = {
        data: AuditLogMapper.mapBankTransactionAuditLogs(
          <BankTransactionAuditLogModel[]>response.body?.valueOf() ?? []),
        total: paginationHeader?.total,
        skip: paginationHeader?.skip,
        take: paginationHeader?.take,
        hasPrevious: paginationHeader?.hasPrevious,
        hasNext: paginationHeader?.hasNext
      };

      return <ServiceResultModel<PaginatedListModel<AuditLog>>>{
        data: paginatedList, success: true, statusCode: response.status
      };
    } catch (error) {
      return ServiceUtilities.handleServiceException(error);
    }
  }

  public async searchBankConnections(
    filter: BankConnectionsFilter,
    orderBy: BankConnectionSortOptions,
    skip?: number,
    take?: number
  ): Promise<ServiceResultModel<PaginatedListModel<BankConnectionModel>>> {
    try {
      const requestUrl = `${this.apiPath}/filter?orderBy=${encodeURIComponent(orderBy)}&skip=${skip ?? 0}&take=${take ?? ''}`;
      const response = await firstValueFrom(
        this.httpClient.post<BankConnectionModel[]>(
          requestUrl,
          JSON.stringify(APISanitizerUtilities.sanitizePayload<BankConnectionsFilter>(filter)),
          { observe: "response", headers: { 'Accept': 'application/json', 'Content-Type': 'application/json' } }
        )
      );

      const paginationHeader: PaginationResponseHeader = JSON.parse(response.headers.get("x-pagination")!);
      const paginatedList: PaginatedListModel<BankConnectionModel> = {
        data: <BankConnectionModel[]>response.body?.valueOf(),
        total: paginationHeader?.total,
        skip: paginationHeader?.skip,
        take: paginationHeader?.take,
        hasPrevious: paginationHeader?.hasPrevious,
        hasNext: paginationHeader?.hasNext
      };

      return <ServiceResultModel>{ data: paginatedList, success: true, statusCode: response.status };
    } catch (error) {
      return ServiceUtilities.handleServiceException(error);
    }
  }
}
